/* Felicitations — shared design-fee cart reconciliation.
   The wedding-stationery design fee is scope-tiered and charged ONCE across the
   STD / INV / OTD product pages. Each calculator calls FelDesign.checkout(...) on
   add-to-cart; this reads the current cart and reconciles to a SINGLE design-deposit
   line (the union of scopes/tiers — never double-charged), adds a $0 Print
   Configuration line per product (print specs as properties, for later invoicing),
   and ensures each one-off add-on (wax / embossing / monogram / illustration) is
   charged at most once.

   Pricing (paid once):           SC      TT
     Save the Dates only          $100    $150
     Invitations (STD incl. free) $200    $300
     Full set (STD+INV+OTD)       $300    $500
*/
(function () {
  'use strict';
  var root = (typeof window !== 'undefined') ? window : (typeof globalThis !== 'undefined') ? globalThis : this;

  // Numeric variant IDs (the Shopify cart API speaks numeric variant ids, not GIDs).
  var DESIGN = {
    SC: { std: 46313543565508, inv: 46313543598276, full: 46313543631044 },
    TT: { std: 46313543663812, inv: 46313543696580, full: 46313543729348 }
  };
  var PRINT_CONFIG = 46313543893188;
  var ADDONS = { wax: 46313543762116, emboss: 46313543794884, monogram: 46313543827652, illust: 46313543860420 };

  // Design-fee price table (paid once) — single source of truth for the calculators' labels/totals.
  var PRICE = {
    SC: { std: 100, inv: 200, full: 300 },
    TT: { std: 150, inv: 300, full: 500 }
  };
  var SCOPE_RANK = { std: 0, inv: 1, full: 2 };
  var TIER_RANK = { SC: 0, TT: 1 };
  var SCOPE_LABEL = {
    std: 'Save the Dates',
    inv: 'Invitations (Save the Dates included)',
    full: 'Full set (Save the Dates + Invitations + On the day)'
  };
  var TIER_LABEL = { SC: 'Studio Collection', TT: 'Tailored Touch' };

  // On-the-Day design tiers (bought from collection product pages): single piece $100, bundle $150,
  // whole on-the-day $200. ONE OTD design line, set to the highest tier present (price-max), capped at
  // $200, and $0 when the full wedding-suite design is already in the cart.
  var OTD_PIECE = { single: 46314221109444, bundle: 46315239243972, whole: 46314221142212 };
  var OTD_PRICE_VAR = { 100: OTD_PIECE.single, 150: OTD_PIECE.bundle, 200: OTD_PIECE.whole };
  var OTD_PIECE_SET = {}; [OTD_PIECE.single, OTD_PIECE.bundle, OTD_PIECE.whole].forEach(function (v) { OTD_PIECE_SET[v] = 1; });

  // Reverse maps.
  var VAR2DS = {};
  Object.keys(DESIGN).forEach(function (t) {
    Object.keys(DESIGN[t]).forEach(function (s) { VAR2DS[DESIGN[t][s]] = { tier: t, scope: s }; });
  });

  function vid(it) { return it.variant_id || it.id; } // cart.js line uses variant_id; be tolerant
  function higher(a, b, rank) { return (rank[a] >= rank[b]) ? a : b; }

  // Scan the cart for the On-the-Day design picture: full-suite covered?, how many distinct INDIVIDUAL pieces
  // (tagged _otd_piece, excluding exclPrintKey), is any OTHER bundle present (tagged _otd_bundle !== exclBundleKey),
  // and the current single OTD design line. Bundle piece lines carry _otd_bundle (NOT _otd_piece) so they never
  // inflate the individual-piece count.
  function otdScan(items, exclPrintKey, exclBundleKey) {
    var s = { suiteFull: false, hasBundle: false, otdDesign: null }, keys = {};
    items.forEach(function (it) {
      var v = vid(it), p = it.properties || {};
      if (VAR2DS[v] && VAR2DS[v].scope === 'full') s.suiteFull = true;
      if (OTD_PIECE_SET[v]) s.otdDesign = { key: it.key, variant: v };
      if (p._otd_bundle) { if (p._otd_bundle !== exclBundleKey) s.hasBundle = true; }
      else if (p._otd_piece && p._print_key && p._print_key !== exclPrintKey) keys[p._print_key] = 1;
    });
    s.nIndiv = Object.keys(keys).length;
    return s;
  }

  // The single OTD design line follows price-max: 1 piece $100 < bundle $150 < 2+ pieces / whole $200 (capped);
  // $0 (no line) when the full wedding-suite design already covers on-the-day.
  function otdTargetVar(scan, addIndiv, addBundle) {
    if (scan.suiteFull) return 0;
    var n = scan.nIndiv + (addIndiv || 0);
    var price = (n >= 2) ? 200 : (n === 1 ? 100 : 0);
    if (scan.hasBundle || addBundle) price = Math.max(price, 150);
    price = Math.min(price, 200);
    return price ? OTD_PRICE_VAR[price] : 0;
  }

  function reconcileOtdDesign(scan, targetVar, dProps, remove, add) {
    if (!targetVar) { if (scan.otdDesign) remove.push(scan.otdDesign.key); return 'covered'; }
    if (!scan.otdDesign) { add.push({ id: targetVar, quantity: 1, properties: dProps }); return 'added'; }
    if (scan.otdDesign.variant !== targetVar) { remove.push(scan.otdDesign.key); add.push({ id: targetVar, quantity: 1, properties: dProps }); return 'upgraded'; }
    return 'covered';
  }

  function otdDesignProps(req) {
    var dProps = { 'Design': 'On-the-day stationery', '_otd_design': '1' };
    if (req.designProps) Object.keys(req.designProps).forEach(function (k) { dProps[k] = req.designProps[k]; });
    return dProps;
  }

  // PURE planner for a SINGLE On-the-Day piece added from a collection product page.
  function planOtdPiece(items, req) {
    var remove = [], add = [];
    var pOld = null;
    items.forEach(function (it) { if (vid(it) === PRINT_CONFIG && it.properties && it.properties._print_key === req.printKey) pOld = it; });
    if (pOld) remove.push(pOld.key);
    var scan = otdScan(items, req.printKey, null);
    var targetVar = otdTargetVar(scan, 1, false);
    var outcome = reconcileOtdDesign(scan, targetVar, otdDesignProps(req), remove, add);
    var pProps = { '_print_key': req.printKey, '_otd_piece': req.pieceId || '1' };
    if (req.printProps) Object.keys(req.printProps).forEach(function (k) { pProps[k] = req.printProps[k]; });
    add.push({ id: PRINT_CONFIG, quantity: 1, properties: pProps });
    return { remove: remove, add: add, outcome: outcome, otdPieces: scan.nIndiv + 1 };
  }

  // PURE planner for an On-the-Day BUNDLE (signage / table) built on its product page — a subset of pieces
  // selected together. Flat $150 design (reconciled by price-max), plus one $0 print line per selected piece.
  function planOtdBundle(items, req) {
    var remove = [], add = [];
    var bk = req.bundleKey || 'bundle';
    items.forEach(function (it) { if (vid(it) === PRINT_CONFIG && it.properties && it.properties._otd_bundle === bk) remove.push(it.key); });
    var scan = otdScan(items, null, bk); // exclude this bundle's own lines (we re-add them)
    var targetVar = otdTargetVar(scan, 0, true);
    var outcome = reconcileOtdDesign(scan, targetVar, otdDesignProps(req), remove, add);
    (req.printItems || []).forEach(function (pi) {
      var pProps = { '_print_key': pi.printKey, '_otd_bundle': bk };
      if (pi.printProps) Object.keys(pi.printProps).forEach(function (k) { pProps[k] = pi.printProps[k]; });
      add.push({ id: PRINT_CONFIG, quantity: 1, properties: pProps });
    });
    return { remove: remove, add: add, outcome: outcome, bundle: bk };
  }

  // PURE planner: given the current cart line items + a request, return the cart ops.
  // Returns { remove:[lineKeys], add:[{id,quantity,properties}], outcome, finalTier, finalScope }.
  function plan(items, req) {
    items = items || [];
    if (req.otdBundle) return planOtdBundle(items, req);
    if (req.otdPiece) return planOtdPiece(items, req);
    var remove = [], add = [];

    // 1) existing design-deposit line (if any)
    var existing = null;
    items.forEach(function (it) { var d = VAR2DS[vid(it)]; if (d) existing = { key: it.key, ds: d, variant: vid(it) }; });

    var reqTier = (req.tier === 'TT') ? 'TT' : 'SC';
    var reqScope = SCOPE_RANK.hasOwnProperty(req.scope) ? req.scope : 'inv';
    var finalTier = existing ? higher(existing.ds.tier, reqTier, TIER_RANK) : reqTier;
    var finalScope = existing ? higher(existing.ds.scope, reqScope, SCOPE_RANK) : reqScope;
    var finalVar = DESIGN[finalTier][finalScope];

    var designProps = { 'Design': TIER_LABEL[finalTier], 'Scope': SCOPE_LABEL[finalScope], '_design': '1' };
    if (req.designProps) Object.keys(req.designProps).forEach(function (k) { designProps[k] = req.designProps[k]; });

    var outcome;
    if (!existing) {
      add.push({ id: finalVar, quantity: 1, properties: designProps });
      outcome = 'added';
    } else if (existing.variant !== finalVar) {
      remove.push(existing.key);
      add.push({ id: finalVar, quantity: 1, properties: designProps });
      outcome = 'upgraded';
    } else {
      outcome = 'covered'; // scope/tier already in the order — charge nothing extra
    }

    // If the suite design now reaches "full", any separate single-OTD-piece design is included → drop it.
    if (finalScope === 'full') {
      items.forEach(function (it) { if (OTD_PIECE_SET[vid(it)]) remove.push(it.key); });
    }

    // 2) Print Configuration line — one per product, keyed so re-configuring updates its specs.
    if (req.printKey) {
      var pOld = null;
      items.forEach(function (it) {
        if (vid(it) === PRINT_CONFIG && it.properties && it.properties._print_key === req.printKey) pOld = it;
      });
      if (pOld) remove.push(pOld.key);
      var pProps = { '_print_key': req.printKey };
      if (req.printProps) Object.keys(req.printProps).forEach(function (k) { pProps[k] = req.printProps[k]; });
      add.push({ id: PRINT_CONFIG, quantity: 1, properties: pProps });
    }

    // 3) one-off add-ons — ensure each is present exactly once.
    (req.addons || []).forEach(function (a) {
      var v = ADDONS[a]; if (!v) return;
      var inCart = items.some(function (it) { return vid(it) === v; });
      var queued = add.some(function (x) { return x.id === v; });
      if (!inCart && !queued) add.push({ id: v, quantity: 1, properties: { '_addon': a } });
    });

    return { remove: remove, add: add, outcome: outcome, finalTier: finalTier, finalScope: finalScope };
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    });
  }

  // IO: read cart → plan → execute (removals then a single batched add) → redirect.
  function checkout(req) {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var ops = plan((cart && cart.items) || [], req);
        var chain = Promise.resolve();
        ops.remove.forEach(function (key) { chain = chain.then(function () { return postJSON('/cart/change.js', { id: key, quantity: 0 }); }); });
        if (ops.add.length) chain = chain.then(function () { return postJSON('/cart/add.js', { items: ops.add }); });
        return chain.then(function () { return ops; });
      })
      .then(function (ops) {
        if (req.redirect !== false) openCartUI();
        return ops;
      });
  }

  // Open the theme's slide-out cart drawer (cart_type: drawer) with fresh contents; fall back to /cart.
  function openCartUI() {
    try {
      if (typeof document !== 'undefined' && document.querySelector && document.querySelector('cart-drawer')) {
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: true } }));
        return;
      }
    } catch (e) { }
    try { root.location.href = '/cart'; } catch (e) { }
  }

  var api = {
    plan: plan, checkout: checkout,
    DESIGN: DESIGN, ADDONS: ADDONS, PRINT_CONFIG: PRINT_CONFIG, PRICE: PRICE, OTD_PIECE: OTD_PIECE,
    SCOPE_RANK: SCOPE_RANK, TIER_RANK: TIER_RANK, SCOPE_LABEL: SCOPE_LABEL, TIER_LABEL: TIER_LABEL
  };
  root.FelDesign = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})();
